// V4.12 Phase F — Canvas mini map: renders the CURRENT canonical search
// on a small MapLibre map. Markers come from the EXISTING /api/explore
// endpoint (the exact same query intelligence as the Heatmap) — the
// widget itself carries only the canonical params.
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Map, { Layer, Source } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPin } from "lucide-react";
import { apiClient } from "../../../api/client";
import { isMapLibreEnabled, maptilerStyleUrl } from "../../../config/mapProvider";
import { locatePlace } from "../../../api/taxonomyApi";
import {
  boundsFromLocateBbox,
  boundsFromPoints,
  plausiblePoints,
} from "../../../utils/geoSanity";

const EUROPE_BBOX = "34,-12,62,32";

async function fetchMarkers(params) {
  const q = {
    bbox: EUROPE_BBOX,
    zoom: 6,
    mode: "markers",
    market_position: "all",
    vertical: params.v === "auto" ? "auto" : "immo",
  };
  ["makes", "models", "fuels", "gearboxes", "body_types", "features",
    "seller_types", "object_types"].forEach((k) => {
    if (params[k]) q[k] = params[k];
  });
  ["year_min", "km_max", "price_min", "price_max"].forEach((k) => {
    if (params[k] !== null && params[k] !== undefined && params[k] !== "") q[k] = params[k];
  });
  // Pipeline review 2026-10-06 — the LOCATION of the chat's search was
  // never forwarded: the map answered for all of Europe while the chat
  // text spoke about one city/quarter (hit counts contradicted each
  // other). `region` + `country` go through the same canonical place
  // resolver as the feed and the alerts.
  if (params.region) q.region = params.region;
  if (params.country) q.country = params.country;
  if (q.vertical === "immo") q.transaction_type = params.tt || "buy";
  const { data } = await apiClient.get("/explore/search", { params: q });
  return data;
}

const PT_LAYER = {
  id: "ai-mini-map-pts",
  type: "circle",
  paint: {
    "circle-color": ["case", ["get", "deal"], "#10b981", "#f59e0b"],
    "circle-radius": [
      "interpolate", ["linear"], ["get", "count"], 1, 4, 50, 10, 500, 16,
    ],
    "circle-opacity": 0.8,
    "circle-stroke-color": "#18181b",
    "circle-stroke-width": 1,
  },
};

export function MiniMapWidget({ widget, variant }) {
  const { t } = useTranslation("ai");
  const params = widget.params || {};
  // V4.14 — public analysis share: markers were snapshotted server-side
  // at share creation (no authenticated explore call needed).
  const snapPts = widget.snapshot_markers;
  const q = useQuery({
    queryKey: ["ai-minimap", params],
    queryFn: () => fetchMarkers(params),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: !snapPts,
  });

  const pts = useMemo(() => {
    if (snapPts) {
      return plausiblePoints(
        snapPts
          .filter((p) => p.lat != null && p.lng != null)
          .map((p) => ({
            lat: p.lat, lng: p.lng, count: p.count || 1, deal: !!p.deal,
          })),
        params.country,
      );
    }
    const d = q.data;
    if (!d) return [];
    const out = [];
    (d.markers || []).forEach((m) => {
      if (m.lat != null && m.lng != null) {
        out.push({ lat: m.lat, lng: m.lng, count: 1,
                   deal: m.market_position === "good_deals" });
      }
    });
    (d.clusters || []).forEach((c) => {
      if (c.lat != null && c.lng != null) {
        out.push({ lat: c.lat, lng: c.lng, count: c.count || 1, deal: false });
      }
    });
    // Placeholder coordinates ((1.0, 1.0), lng 111.0) must never define
    // the viewport — they put the map in the Indian Ocean.
    return plausiblePoints(out, params.country);
  }, [q.data, snapPts, params.country]);

  // The resolved place carries the authoritative viewport (same resolver
  // the chat used), so the map zooms to the quarter/city the answer
  // talks about instead of the extent of whatever markers came back.
  const locQ = useQuery({
    queryKey: ["ai-minimap-locate", params.region, params.country, params.v],
    queryFn: () => locatePlace(
      params.region, params.country || null,
      params.v === "auto" ? "auto" : "immo"),
    enabled: !snapPts && !!params.region,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const bounds = useMemo(() => {
    const placeBounds = locQ.data?.count
      ? boundsFromLocateBbox(locQ.data.bbox, 0.01, params.country)
      : null;
    return placeBounds || boundsFromPoints(pts);
  }, [pts, locQ.data, params.country]);

  const geojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: pts.map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { count: p.count, deal: p.deal },
      })),
    }),
    [pts],
  );

  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
  });
  qs.set("src", "ai");
  // The chat's result-set total is authoritative; the map can only draw
  // the subset that carries usable coordinates. Showing the map's own
  // count as THE count made text and map contradict each other.
  const total = snapPts ? widget.snapshot_total
    : (typeof widget.count === "number" ? widget.count : q.data?.total_count);
  const geoCount = snapPts ? null : q.data?.total_count;
  const geoGap = typeof total === "number" && typeof geoCount === "number"
    && geoCount < total;

  return (
    <div
      data-testid="ai-widget-mini-map"
      className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/70"
    >
      <div className="flex items-center gap-2 px-3 pb-1.5 pt-2.5 text-xs uppercase tracking-wide text-zinc-500">
        <MapPin className="h-3.5 w-3.5 text-amber-400/80" />
        <span className="flex-1 truncate">
          {t("widgets.map")}{widget.label ? ` · ${widget.label}` : ""}
        </span>
        {typeof total === "number" && (
          <span className="text-zinc-400" data-testid="ai-widget-mini-map-count">
            {total.toLocaleString("de-AT")}{!snapPts && q.data?.total_count_capped ? "+" : ""} {t("widgets.hits")}
          </span>
        )}
      </div>
      {geoGap && (
        <div
          data-testid="ai-widget-mini-map-geo-note"
          className="px-3 pb-1 text-[10px] text-zinc-500"
        >
          {t("widgets.geoCoverage", { count: geoCount })}
        </div>
      )}
      <div className="h-52 sm:h-60 bg-zinc-950/60">
        {!snapPts && q.isLoading ? (
          <div className="flex h-full items-center justify-center text-xs text-zinc-600">
            {t("widgets.mapLoading")}
          </div>
        ) : pts.length && bounds && isMapLibreEnabled() ? (
          <Map
            key={pts.length}
            initialViewState={{ bounds, fitBoundsOptions: { padding: 36, maxZoom: 11 } }}
            mapStyle={maptilerStyleUrl()}
            style={{ height: "100%", width: "100%", background: "#18181b" }}
            attributionControl={{ compact: true }}
            scrollZoom={false}
            dragRotate={false}
            touchPitch={false}
          >
            {React.createElement(
              Source,
              { key: "ai-mini-map-src", id: "ai-mini-map-src", type: "geojson", data: geojson },
              React.createElement(Layer, PT_LAYER),
            )}
          </Map>
        ) : (
          <div className="flex h-full items-center justify-center px-4 text-center text-xs text-zinc-600">
            {t("widgets.noGeoHits")}
          </div>
        )}
      </div>
      {variant !== "public" && (
        <div className="px-3 py-2">
          <Link
            to={`/heatmap?${qs.toString()}`}
            data-testid="ai-widget-mini-map-open"
            className="inline-flex items-center gap-1.5 text-xs text-amber-300 transition hover:text-amber-200"
          >
            <MapPin className="h-3.5 w-3.5" />
            {t("widgets.openOnHeatmap")}
          </Link>
        </div>
      )}
    </div>
  );
}
